import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import type { OAuthAuthorizationDetails } from "@supabase/auth-js";
import { useAuth } from "../context/AuthContext";
import { useSignInModal } from "../context/SignInModalContext";
import { supabase } from "../lib/supabaseClient";
import "./OAuthConsent.scss";

const OAuthConsent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { openSignInModal } = useSignInModal();
  const [details, setDetails] = useState<OAuthAuthorizationDetails | null>(
    null,
  );
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authorizationId = searchParams.get("authorization_id");

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      openSignInModal();
    }
  }, [authLoading, isAuthenticated, openSignInModal]);

  useEffect(() => {
    if (!isAuthenticated || !authorizationId) return;

    setLoadingDetails(true);
    supabase.auth.oauth
      .getAuthorizationDetails(authorizationId)
      .then(({ data, error: detailsError }) => {
        if (detailsError) {
          setError(detailsError.message);
          return;
        }
        if (data?.redirect_url) {
          window.location.href = data.redirect_url;
          return;
        }
        setDetails(data);
      })
      .catch(() => setError("Failed to load authorization details."))
      .finally(() => setLoadingDetails(false));
  }, [isAuthenticated, authorizationId]);

  if (!authorizationId) {
    return (
      <div className="oauth-consent-container">
        <div className="oauth-consent-card">
          <div className="error-message">Invalid authorization request.</div>
        </div>
      </div>
    );
  }

  const handleApprove = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data, error: approveError } =
        await supabase.auth.oauth.approveAuthorization(authorizationId);
      if (approveError) {
        setError(approveError.message);
        return;
      }
      if (data?.redirect_url) {
        window.location.href = data.redirect_url;
      }
    } catch {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeny = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data } =
        await supabase.auth.oauth.denyAuthorization(authorizationId);
      if (data?.redirect_url) {
        window.location.href = data.redirect_url;
        return;
      }
    } catch {
      // fall through to home navigation
    }
    navigate("/");
  };

  if (authLoading || loadingDetails) {
    return (
      <div className="oauth-consent-container">
        <div className="oauth-consent-card">
          <p className="oauth-loading">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="oauth-consent-container">
        <div className="oauth-consent-card">
          <h2>Sign in to continue</h2>
          <p className="oauth-consent-description">
            You need to sign in before authorizing this request.
          </p>
          <button className="oauth-approve-button" onClick={openSignInModal}>
            Sign in
          </button>
        </div>
      </div>
    );
  }

  const scopes = details?.scope ? details.scope.split(" ").filter(Boolean) : [];

  return (
    <div className="oauth-consent-container">
      <div className="oauth-consent-card">
        {details?.client.logo_uri && (
          <div className="oauth-consent-logo">
            <img
              src={details.client.logo_uri}
              alt={details.client.name}
              className="oauth-logo-img"
            />
          </div>
        )}
        <h2>Authorize {details?.client.name ?? "Application"}</h2>
        {details?.user.email && (
          <p className="oauth-consent-user">
            as <strong>{details.user.email}</strong>
          </p>
        )}
        {scopes.length > 0 && (
          <ul className="oauth-scopes">
            {scopes.map((scope) => (
              <li key={scope}>{scope}</li>
            ))}
          </ul>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="oauth-consent-actions">
          <button
            className="oauth-approve-button"
            onClick={handleApprove}
            disabled={submitting}
          >
            {submitting ? "Authorizing..." : "Allow"}
          </button>
          <button
            className="oauth-deny-button"
            onClick={handleDeny}
            disabled={submitting}
          >
            Deny
          </button>
        </div>
      </div>
    </div>
  );
};

export default OAuthConsent;
